import { Checkbox } from '@crono-one/design-system';
import { Avatar } from '@mui/material';
import DebouncedSearchInput from 'components/DebouncedSearchInput';
import { FeConstants } from 'constants/FeConstants';

import { fullName } from 'crono-fe-common/utils';
import { useSearchUsersInfiniteScroll } from 'hooks/services/user/useSearchUsers';
import { useInfiniteScrollSentinel } from 'hooks/useInfiniteScrollSentinel';
import { useMemo, useRef, useState } from 'react';
import { stringAvatarProspect } from 'utils/formatUtils';
import { colors } from 'crono-fe-common/theme';
import { TeamsBadgeList } from './TeamsBadgeList';

interface TeamsUsersSearchProps {
  selectedUserIds: number[];
  onChangeSelectedUserIds: (userIds: number[]) => void;
}

export const TeamsUsersSearch = (props: TeamsUsersSearchProps) => {
  const { selectedUserIds, onChangeSelectedUserIds } = props;

  const [search, setSearch] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: usersData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSearchUsersInfiniteScroll({
    limit: FeConstants.usersPageLimit,
    text: search || undefined,
    sortType: 'Name',
    withTeams: true,
  });

  const users = useMemo(
    () => usersData?.pages.flatMap((page) => page.data.data) ?? [],
    [usersData],
  );

  const sentinelRef = useInfiniteScrollSentinel({
    root: scrollContainerRef,
    rootMargin: '0px 0px 80px 0px',
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  function handleSelectUser(userId: number) {
    if (selectedUserIds.includes(userId)) {
      onChangeSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      onChangeSelectedUserIds([...selectedUserIds, userId]);
    }
  }

  function handleSelectAllUsers() {
    if (selectedUserIds.length === users.length) {
      onChangeSelectedUserIds([]);
    } else {
      onChangeSelectedUserIds(users.map((user) => user.id));
    }
  }

  return (
    <div className="flex flex-col overflow-hidden flex-1">
      <div className="mb-6">
        <label className="text-sm font-semibold" htmlFor="usersSearch">
          Add users
        </label>
        <DebouncedSearchInput
          value={search}
          onSearch={setSearch}
          placeholder="Search users..."
          className="w-full"
          id="usersSearch"
        />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex flex-col overflow-y-auto pr-3 flex-1"
      >
        {isLoading ? (
          <>
            {new Array(5).fill(null).map((_, index) => (
              <div key={index} className="flex items-center gap-3 px-2 py-1">
                <div className="bg-neutral-300 animate-pulse h-4 w-4 rounded-sm shrink-0" />
                <div className="bg-neutral-300 animate-pulse h-6 w-6 rounded-full shrink-0" />
                <div className="bg-neutral-300 animate-pulse h-4 flex-1 rounded-3xl" />
              </div>
            ))}
          </>
        ) : users.length === 0 ? (
          <div className="flex justify-center">
            <p className="text-sm font-medium px-2 py-1">No users found</p>
          </div>
        ) : (
          <>
            <div
              onClick={handleSelectAllUsers}
              className="flex items-center gap-3 px-2 py-1 cursor-pointer transition-colors hover:bg-gray-100 rounded-sm min-h-8"
            >
              <Checkbox
                checked={
                  selectedUserIds.length > 0
                    ? selectedUserIds.length === users.length
                      ? true
                      : 'indeterminate'
                    : false
                }
              />

              <p className="text-xs font-medium text-gray-700">All users</p>
            </div>

            {users.map((user) => (
              <div
                className="flex items-center gap-3 px-2 py-1 cursor-pointer transition-colors hover:bg-gray-100 rounded-sm"
                key={user.id}
                onClick={() => handleSelectUser(user.id)}
              >
                <Checkbox checked={selectedUserIds.includes(user.id)} />

                <Avatar
                  sx={{
                    width: '24px',
                    height: '24px',
                    fontSize: '12px',
                    color: colors.darkGreen,
                    backgroundColor: colors.primaryLight,
                  }}
                  alt={fullName(user.firstName, user.lastName)}
                >
                  {stringAvatarProspect(user)}
                </Avatar>

                <p className="text-sm truncate shrink-0 max-w-[40%]">
                  {user.firstName} {user.lastName}
                </p>

                <div className="flex-1 min-w-0">
                  <TeamsBadgeList
                    teams={user.teams ?? []}
                    collisionBoundary={scrollContainerRef.current}
                    size="sm"
                    alignEnd
                  />
                </div>
              </div>
            ))}

            <div ref={sentinelRef} className="shrink-0 min-h-px" />
          </>
        )}
      </div>
    </div>
  );
};
